/**
 * usePersonalEvents — 내 개인 일정·할 일 구독 + 추가·수정·삭제 (B′ 미리보기).
 *   일정(kind "event")과 할 일(kind "todo")을 한 목록으로 돌려준다 — 화면이 kind 로 나눠 쓴다.
 *
 * 저장: users/{uid}/personal_events (services/personalEvents.repository.ts).
 * ⚠️ rules 미배포 상태에서는 구독이 permission-denied 로 끝난다 → 그때부터 **미리보기 로컬 저장**
 *    (localStorage, 이 브라우저에만)으로 대체하고 mode="local" 을 돌려준다. 화면은 이 값으로 작은 안내를 띄운다.
 *    로컬 모드로 바뀐 뒤에는 Firestore 쓰기를 시도하지 않는다(거부된 경로를 계속 두드리지 않게).
 * 정렬은 클라이언트(sortEvents) — orderBy(serverTimestamp) 를 쓰지 않는다.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { personalEventsRepo } from "@/services/personalEvents.repository";
import {
  coercePersonalEvent,
  sortEvents,
  type PersonalEvent,
  type PersonalEventInput,
} from "@/domain/personalEvent";

export type PersonalEventsMode = "loading" | "remote" | "local" | "error";

const lsKey = (uid: string) => `rmc.preview.personalEvents.${uid}`;

function readLocal(uid: string): PersonalEvent[] {
  try {
    const raw = localStorage.getItem(lsKey(uid));
    if (!raw) return [];
    const arr: unknown = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x) => (x && typeof x === "object" && typeof (x as { id?: unknown }).id === "string"
        ? coercePersonalEvent((x as { id: string }).id, x as Record<string, unknown>)
        : null))
      .filter((e): e is PersonalEvent => e !== null);
  } catch {
    return [];
  }
}
function writeLocal(uid: string, list: PersonalEvent[]) {
  try { localStorage.setItem(lsKey(uid), JSON.stringify(list)); } catch { /* private mode 등 — 이번 창에서만 유지 */ }
}

function isDenied(e: unknown): boolean {
  return !!e && typeof e === "object" && (e as { code?: unknown }).code === "permission-denied";
}

function localEvent(id: string, input: PersonalEventInput, createdAt?: number): PersonalEvent {
  const now = Date.now();
  return {
    id,
    kind: input.kind,
    done: input.kind === "todo" && input.done,
    title: input.title,
    date: input.date,
    all_day: input.all_day,
    start_time: input.all_day ? undefined : input.start_time,
    end_time: input.all_day ? undefined : input.end_time,
    memo: input.memo,
    calendar_id: input.calendar_id,
    created_at: createdAt ?? now,
    updated_at: now,
  };
}

interface RemoteState {
  uid: string;
  mode: Exclude<PersonalEventsMode, "loading">;
  list: PersonalEvent[];
  error?: string;
}

export function usePersonalEvents() {
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const [remote, setRemote] = useState<RemoteState | null>(null);
  const [local, setLocal] = useState<{ uid: string; list: PersonalEvent[] } | null>(null);

  useEffect(() => {
    if (!uid) return;
    let alive = true;
    const unsub = personalEventsRepo.watch(
      uid,
      (list) => { if (alive) setRemote({ uid, mode: "remote", list }); },
      (err) => {
        if (!alive) return;
        if (isDenied(err)) {
          setLocal({ uid, list: readLocal(uid) });
          setRemote({ uid, mode: "local", list: [] });
        } else {
          setRemote({ uid, mode: "error", list: [], error: err.message });
        }
      },
    );
    return () => { alive = false; unsub(); };
  }, [uid]);

  const cur = remote && remote.uid === uid ? remote : null;
  const mode: PersonalEventsMode = cur ? cur.mode : "loading";
  const localList = useMemo(() => (local && local.uid === uid ? local.list : []), [local, uid]);

  const events = useMemo(
    () => sortEvents(mode === "local" ? localList : (cur?.list ?? [])),
    [mode, localList, cur],
  );

  const toLocal = useCallback(
    (next: PersonalEvent[]) => {
      writeLocal(uid, next);
      setLocal({ uid, list: next });
    },
    [uid],
  );

  /** Firestore 쓰기가 거부되면 로컬 모드로 넘어가고 같은 작업을 로컬에 한다 */
  const fallBack = useCallback(
    (run: (base: PersonalEvent[]) => PersonalEvent[]) => {
      const base = readLocal(uid);
      toLocal(run(base));
      setRemote({ uid, mode: "local", list: [] });
    },
    [uid, toLocal],
  );

  const add = useCallback(
    async (input: PersonalEventInput) => {
      if (!uid) throw new Error("로그인이 필요합니다.");
      const id = `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const runLocal = (base: PersonalEvent[]) => [...base, localEvent(id, input)];
      if (mode === "local") { toLocal(runLocal(localList)); return; }
      try {
        await personalEventsRepo.add(uid, input);
      } catch (e) {
        if (!isDenied(e)) throw e;
        fallBack(runLocal);
      }
    },
    [uid, mode, localList, toLocal, fallBack],
  );

  const update = useCallback(
    async (id: string, input: PersonalEventInput) => {
      if (!uid) throw new Error("로그인이 필요합니다.");
      const runLocal = (base: PersonalEvent[]) =>
        base.map((e) => (e.id === id ? localEvent(id, input, e.created_at) : e));
      if (mode === "local") { toLocal(runLocal(localList)); return; }
      try {
        await personalEventsRepo.update(uid, id, input);
      } catch (e) {
        if (!isDenied(e)) throw e;
        fallBack(runLocal);
      }
    },
    [uid, mode, localList, toLocal, fallBack],
  );

  const remove = useCallback(
    async (id: string) => {
      if (!uid) throw new Error("로그인이 필요합니다.");
      const runLocal = (base: PersonalEvent[]) => base.filter((e) => e.id !== id);
      if (mode === "local") { toLocal(runLocal(localList)); return; }
      try {
        await personalEventsRepo.remove(uid, id);
      } catch (e) {
        if (!isDenied(e)) throw e;
        fallBack(runLocal);
      }
    },
    [uid, mode, localList, toLocal, fallBack],
  );

  return { events, mode, error: cur?.error ?? null, add, update, remove };
}
